/**
 * Sector editions of the funding page. Same components, same application — different language,
 * different numbers, different questions. Each lives on its own keyword URL and still accepts an
 * introducer slug (`/property-finance/their-firm-a1b2`), so a specialist broker can share the
 * edition that matches their book and keep the credit.
 */
export const SECTOR_LIST = ["property", "healthcare", "manufacturing", "hospitality"];

export const SECTORS = {
  property: {
    key: "property",
    path: "/property-finance",
    label: "Property & development",
    short: "Property",
    eyebrow: "For property professionals",
    h1: ["Development, bridging", "and term debt —", "placed on the exit."],
    lede: "Connexis places property debt with the funders who underwrite it properly: development and "
        + "refurbishment, auction and chain-break bridging, portfolio refinance and long-term commercial "
        + "mortgages. £100,000 to £500m and beyond, on the exit you can actually evidence.",
    proof: [
      { label: "Terms on straightforward cases", value: "24–72 hours" },
      { label: "Facility range", value: "£100k – £500m+" },
      { label: "Cost to you", value: "Nothing" },
    ],
    types: ["Development finance", "Bridging", "Auction finance", "Refurbishment", "Commercial mortgages",
            "Portfolio refinance", "Second charge", "Mezzanine"],
    productsLede: "Property debt is a structure question before it's a price question. These are the routes we "
                + "place, and most schemes end up using two of them in sequence.",
    stories: [
      { amount: "£18m", product: "Portfolio refinance", who: "Mixed-use investor, 14 assets",
        body: "Exited an expensive term facility across a mixed commercial and residential portfolio, consolidated "
            + "onto one lender with a five-year fix and released equity for the next two acquisitions.",
        beat: "Terms in 6 days" },
      { amount: "£6.4m", product: "Development finance", who: "Residential scheme, 22 units",
        body: "Funded land and build against GDV with a monitoring surveyor in place. Structured as senior debt "
            + "with mezzanine behind it so the developer held equity rather than giving away a share.",
        beat: "Credit-backed in 9 days" },
      { amount: "£1.2m", product: "Bridging", who: "Auction purchase, South East",
        body: "28 days to complete. Two funders responded inside 48 hours; the cheaper needed a valuation the "
            + "agent turned round in three days, and the exit was a term facility we'd already agreed.",
        beat: "Completed with a week spare" },
      { amount: "£850k", product: "Refurbishment + exit",
        who: "Commercial to residential conversion",
        body: "Staged drawdowns against works, refinanced onto a buy-to-let facility on practical completion — "
            + "both sides of the deal placed at the outset rather than scrambling at the end.",
        beat: "Both facilities agreed up front" },
    ],
    faqs: [
      { q: "How quickly can bridging complete?",
        a: "Two to three weeks is realistic on a clean title with a valuation instructed early; a fortnight is "
         + "achievable where solicitors are already engaged and searches are in hand. Auction deadlines drive "
         + "everything, so tell us the completion date at the outset and we'll only approach funders who can hit it." },
      { q: "What loan-to-value should I expect?",
        a: "Bridging generally runs to 70–75% of value, sometimes higher with additional security. Development is "
         + "sized on both cost and GDV — commonly up to 65–70% of GDV, or 80–90% of total cost with mezzanine "
         + "layered behind senior debt. Your track record moves these numbers more than anything else." },
      { q: "Do you fund land without planning?",
        a: "Yes, though the market narrows and pricing reflects the risk. Strategic land, options and sites with "
         + "outline consent are all fundable with the right story and exit; pre-planning leverage will be modest." },
    ],
    close: { title: ["Send us the scheme,", "the numbers and the exit."],
             body: "Valuation, legals and monitoring set the timetable on property, so the earlier a funder sees it "
                 + "the more of the timetable you keep. Four minutes to submit, no credit search to start." },
    seo: {
      title: "Property & development finance — Connexis",
      desc: "Development finance, bridging, auction and commercial mortgages placed with UK funders. £100k to "
          + "£500m+, priced on the exit, with no cost to the borrower.",
    },
  },

  healthcare: {
    key: "healthcare",
    path: "/healthcare-finance",
    label: "Healthcare & care homes",
    short: "Healthcare",
    eyebrow: "For healthcare operators",
    h1: ["Funding for care,", "clinical and", "medical operators."],
    lede: "Care homes, supported living, dental and veterinary groups, primary care and private clinics. Connexis "
        + "places acquisition, refinance, fit-out and equipment debt with the funders who understand CQC ratings, "
        + "occupancy and fee income — and who lend on going concern, not just bricks.",
    proof: [
      { label: "Sector funders on panel", value: "Specialist" },
      { label: "Facility range", value: "£250k – £500m+" },
      { label: "Cost to you", value: "Nothing" },
    ],
    types: ["Acquisition finance", "Care home mortgages", "Refinance", "Fit-out & capex",
            "Medical equipment", "Working capital", "Development", "Structured debt"],
    productsLede: "Healthcare lending turns on the operation as much as the asset — registration, occupancy, "
                + "staffing and fee mix. These are the routes we place, frequently in combination.",
    stories: [
      { amount: "£18m", product: "Portfolio refinance", who: "Care group, four registered homes",
        body: "Moved away from an incumbent bank that wanted the whole structure reworked. Placed with one lead "
            + "funder on a seven-year term, sized on EBITDAR, with headroom held back for the fifth home.",
        beat: "Terms in 6 days" },
      { amount: "£4.9m", product: "Acquisition finance", who: "Dental group, three-practice bolt-on",
        body: "Funded a bolt-on acquisition against combined practice income, with goodwill lending from a funder "
            + "that prices dental on recurring NHS and private revenue rather than property alone.",
        beat: "Offer in 11 days" },
      { amount: "£2.1m", product: "Development & fit-out", who: "Supported living conversion",
        body: "Staged funding for conversion and registration, then a term facility on the operating asset once "
            + "occupancy reached the agreed level — both legs agreed before works started.",
        beat: "Drawdowns on programme" },
      { amount: "£620k", product: "Equipment & capex", who: "Private clinic, imaging suite",
        body: "Diagnostic equipment funded over five years on hire purchase, secured on the kit itself so the "
            + "clinic's working capital and existing banking lines stayed untouched.",
        beat: "Decision in 36 hours" },
    ],
    faqs: [
      { q: "Does a CQC rating affect what I can borrow?",
        a: "It affects appetite and pricing rather than closing the door. 'Good' and 'Outstanding' attract the "
         + "keenest terms; 'Requires improvement' is fundable with a credible action plan and the right funder, "
         + "though gearing will be tighter. We'll tell you where you sit before anything goes out." },
      { q: "Can you fund the goodwill, not just the property?",
        a: "Yes. Dental, veterinary and clinical acquisitions are routinely funded on recurring income and goodwill, "
         + "with specialist lenders sizing against EBITDA or practice revenue. Property-only lenders will always "
         + "look thinner on these deals, which is exactly why the panel matters." },
      { q: "We're buying our first home — is that fundable?",
        a: "It is, with the right management team behind it. First-time operators with sector experience, a "
         + "registered manager in place and a sensible deposit are lendable; funders will want to see the "
         + "operational plan as closely as the valuation." },
    ],
    close: { title: ["Tell us the operation,", "not just the building."],
             body: "Occupancy, fee mix, registration and staffing decide healthcare terms. Send it once and we'll "
                 + "place it with funders who read those numbers properly — no cost to you either way." },
    seo: {
      title: "Healthcare & care home finance — Connexis",
      desc: "Acquisition, refinance, development and equipment funding for care homes, dental, veterinary and "
          + "clinical operators. £250k to £500m+, placed with specialist UK funders.",
    },
  },

  manufacturing: {
    key: "manufacturing",
    path: "/manufacturing-finance",
    label: "Manufacturing & industrial",
    short: "Manufacturing",
    eyebrow: "For manufacturers & industrial groups",
    h1: ["Plant, premises", "and working capital", "for makers."],
    lede: "Manufacturing, engineering, fabrication and industrial services. Connexis funds capital equipment, "
        + "refinances the plant you already own, releases the cash held in your sales ledger and places the term "
        + "debt behind an acquisition — £25,000 to £500m and beyond.",
    proof: [
      { label: "Asset decisions", value: "Often 24 hours" },
      { label: "Facility range", value: "£25k – £500m+" },
      { label: "Cost to you", value: "Nothing" },
    ],
    types: ["Asset finance", "Asset refinance", "Invoice finance", "Trade & import finance",
            "Industrial mortgages", "Capex facilities", "Acquisition debt", "VAT & tax"],
    productsLede: "Industrial balance sheets usually hold more security than the business realises. These are the "
                + "routes we place — and refinancing existing plant is the one most people overlook.",
    stories: [
      { amount: "£4.2m", product: "Asset refinance + capex", who: "Industrial manufacturer, Midlands",
        body: "Released capital tied up in existing plant and funded a new line inside the same facility. Security "
            + "sat on the assets rather than the trading business, which kept the covenant package light.",
        beat: "Terms in 31 hours" },
      { amount: "£3.5m", product: "Acquisition debt", who: "Engineering group, competitor buy-in",
        body: "Senior debt with an asset-based line behind it to fund a competitor acquisition, sized on combined "
            + "EBITDA and the target's machinery rather than a single lender's blanket policy.",
        beat: "Structured in 3 weeks" },
      { amount: "£1.1m", product: "Trade & import finance", who: "Component importer",
        body: "Paid overseas suppliers up front against confirmed orders, with the facility repaid when the "
            + "finished goods invoiced out — stock financed without draining the overdraft.",
        beat: "Facility live in 8 days" },
      { amount: "£450k", product: "Invoice finance", who: "Precision subcontractor",
        body: "Large OEM customers on 60-day terms against weekly wages. A confidential receivables facility "
            + "turned approved invoices into same-day cash and scaled with the order book.",
        beat: "Live inside a week" },
    ],
    faqs: [
      { q: "Can I raise money against machinery I already own?",
        a: "Yes — asset refinance is one of the most under-used routes in industry. A funder values the plant, "
         + "advances against it and takes security on the assets, which puts capital back on the balance sheet "
         + "without touching your bank facilities. Terms typically run one to seven years." },
      { q: "What about imported or bespoke machinery?",
        a: "Both are fundable. Imported kit can be paid for on order with the facility running from delivery or "
         + "commissioning, and bespoke or part-built machinery is placed with funders who take staged invoices "
         + "from the manufacturer. Lead times need to be on the table early." },
      { q: "Do you fund tooling, fit-out and installation?",
        a: "Often, yes — installation, commissioning and associated tooling can usually be wrapped into the same "
         + "facility as the asset, though soft costs attract lower advance rates than the hard asset itself." },
    ],
    close: { title: ["Tell us what you're buying,", "or what you already own."],
             body: "Capital equipment, a new unit, an acquisition or the cash held in your ledger — one "
                 + "application covers it. Four minutes to submit, and no credit search to get an answer." },
    seo: {
      title: "Manufacturing & industrial finance — Connexis",
      desc: "Asset finance and refinance, invoice finance, trade finance and acquisition debt for UK manufacturers "
          + "and engineering groups. £25k to £500m+, no cost to the borrower.",
    },
  },

  hospitality: {
    key: "hospitality",
    path: "/hospitality-finance",
    label: "Hospitality & leisure",
    short: "Hospitality",
    eyebrow: "For hospitality & leisure operators",
    h1: ["Funding that", "understands", "a trading season."],
    lede: "Hotels, pubs, restaurants, sites and leisure operators. Connexis places acquisition and refinance debt, "
        + "fit-out and kitchen capex, and working capital that flexes with takings — with funders who read "
        + "seasonality as normal trading rather than a red flag.",
    proof: [
      { label: "Advances on takings", value: "Same week" },
      { label: "Facility range", value: "£25k – £250m+" },
      { label: "Cost to you", value: "Nothing" },
    ],
    types: ["Acquisition finance", "Commercial mortgages", "Refinance", "Fit-out & capex",
            "Revenue-based advances", "Kitchen & equipment", "Working capital", "VAT & tax"],
    productsLede: "Trade that moves with the calendar needs repayment that moves with it too. These are the routes "
                + "we place across hospitality and leisure.",
    stories: [
      { amount: "£7.8m", product: "Acquisition & refinance", who: "Boutique hotel group, two sites",
        body: "Bought a second hotel and refinanced the first onto one facility, sized on trading EBITDA with a "
            + "capex tranche held back for bedroom refurbishment in the low season.",
        beat: "Terms in 8 days" },
      { amount: "£1.6m", product: "Commercial mortgage", who: "Multi-site restaurant operator",
        body: "Purchased the freehold of a leasehold site it had traded from for nine years, cutting the rent bill "
            + "and putting an appreciating asset on the balance sheet.",
        beat: "Offer in 12 days" },
      { amount: "£380k", product: "Fit-out & equipment", who: "Pub company, kitchen rebuild",
        body: "Funded a full kitchen rebuild across two houses on asset finance, with payments structured to start "
            + "after the works were finished and trading again.",
        beat: "Approved in 48 hours" },
      { amount: "£120k", product: "Revenue-based advance", who: "Seasonal coastal operator",
        body: "Stock and staffing ahead of the summer run-in, repaid as an agreed share of card takings — so a "
            + "quiet February costs a fraction of a busy August.",
        beat: "Decision same day" },
    ],
    faqs: [
      { q: "Our trade is seasonal — does that count against us?",
        a: "Not with the right funder. Seasonality is normal in this sector, and lenders who know it will look at "
         + "annual performance and structure repayments around your quiet months — including facilities repaid as "
         + "a share of takings rather than a fixed monthly amount." },
      { q: "Can you fund a leasehold site?",
        a: "Yes, though the unexpired term matters. Long leases support asset and cashflow lending comfortably; "
         + "shorter leases narrow the field and bring more focus onto trading figures and personal covenant. "
         + "Freehold purchases open up the mortgage market properly." },
      { q: "We're a new operator taking on our first site — is that fundable?",
        a: "It can be, with relevant operating experience, a realistic trading plan and a meaningful deposit. "
         + "Expect a higher equity contribution and more focus on the team than on the site itself. We'll tell "
         + "you what is realistic before you spend a month chasing it." },
    ],
    close: { title: ["Tell us the site,", "and how it actually trades."],
             body: "Acquisition, freehold purchase, refurbishment or a working capital line for the season — one "
                 + "application covers all of it, with no credit search to start the conversation." },
    seo: {
      title: "Hospitality & leisure finance — Connexis",
      desc: "Acquisition, commercial mortgages, fit-out, equipment and revenue-based funding for UK hotels, pubs, "
          + "restaurants and leisure operators. £25k to £250m+, no cost to the borrower.",
    },
  },
};
